// SPDX-License-Identifier: Elastic-2.0
/**
 * matterMock — MSW handlers for the F-352 Matter commissioning flow.
 *
 * Provides a *stateful* mock so the Matter pairing flow (and T-007 tests)
 * can drive a single job from STARTING through IN_PROGRESS to a terminal
 * state across successive polls:
 *
 *   POST /onboarding/matter/commission → STARTING job (fixed job_id)
 *   GET  /onboarding/jobs/{id}         → advances on every call:
 *                                        starting → in_progress → succeeded
 *   DELETE /onboarding/jobs/{id}       → cancelled
 *
 * URL patterns use regex on the host (like onboardingMock) so they match
 * both http://localhost:8200/api/v1/onboarding/* (vitest) and
 * /api/mirlai/v1/onboarding/* (single-host add-on).
 *
 * ── Ordering vs. the F-351 onboardingMock ─────────────────────────────────
 * onboardingMock.ts ALSO registers a generic `GET /onboarding/jobs/{id}`
 * handler (and a DELETE). MSW resolves with the FIRST matching handler, so
 * `matterHandlers` MUST be registered BEFORE `onboardingHandlers` for the
 * stateful Matter machine to win in Matter-flow tests/dev. To make this
 * robust regardless of registration order, the GET/DELETE handlers here
 * *only* respond for the Matter job id (MATTER_JOB_ID) and otherwise return
 * `passthrough()`, letting the generic F-351 handler take over for any other
 * job id. So even if someone wires them in the "wrong" order, the matter job
 * still resolves to the stateful mock and non-matter jobs fall through.
 */
import { HttpResponse, http, passthrough } from "msw";

import type { CommissioningJob } from "@/api/onboardingClient";

const MATTER_COMMISSION_RE = /\/onboarding\/matter\/commission$/;
const JOB_RE = /\/onboarding\/jobs\/([^/]+)$/;

/** Fixed job id the Matter commission POST always returns. */
export const MATTER_JOB_ID = "matter-job-1";

/** Forced terminal outcomes a test can request before polling. */
type ForcedOutcome = "failed" | "timed_out";

/** Special pairing codes the POST recognises to force a failure outcome. */
const FORCE_FAILED_CODE = "FORCE-FAILED";
const FORCE_TIMEOUT_CODE = "FORCE-TIMEOUT";

/**
 * Stateful poll counter per job id. Each GET increments it so the same job
 * advances through its lifecycle. Keyed by job_id so multiple jobs could be
 * tracked independently.
 */
const pollCounts = new Map<string, number>();

/** Forced outcome per job id, set by the POST (special code) or a test. */
const forcedOutcomes = new Map<string, ForcedOutcome>();

const STARTED_AT = "2026-06-01T12:00:00Z";

function baseJob(): CommissioningJob {
  return {
    job_id: MATTER_JOB_ID,
    kind: "matter",
    state: "starting",
    progress: 0.0,
    code_kind: null,
    device_ref: null,
    error: null,
    events: ["Starting…"],
    started_at: STARTED_AT,
    updated_at: STARTED_AT,
  };
}

/**
 * Reset all mock state. Call in `beforeEach`/`afterEach` so each test starts
 * with a fresh STARTING job and no forced outcome.
 */
export function resetMatterMock(): void {
  pollCounts.clear();
  forcedOutcomes.clear();
}

/**
 * Force the matter job to a terminal failure outcome on its next terminal
 * poll. Call before polling to exercise the error/timeout UI.
 */
export function forceMatterOutcome(
  outcome: ForcedOutcome,
  jobId: string = MATTER_JOB_ID,
): void {
  forcedOutcomes.set(jobId, outcome);
}

/** Compute the job snapshot for the Nth poll (0-indexed). */
function jobForPoll(jobId: string, poll: number): CommissioningJob {
  const job = baseJob();
  job.job_id = jobId;

  // Poll 0: STARTING (as just created).
  if (poll <= 0) {
    return job;
  }

  // Poll 1: IN_PROGRESS at 50%.
  if (poll === 1) {
    return {
      ...job,
      state: "in_progress",
      progress: 0.5,
      events: ["Starting…", "Connecting…"],
    };
  }

  // Poll >=2: terminal. Honour a forced failure/timeout if requested.
  const forced = forcedOutcomes.get(jobId);
  if (forced === "failed") {
    return {
      ...job,
      state: "failed",
      progress: 0.5,
      error: "Commissioning failed",
      events: ["Starting…", "Connecting…", "Failed"],
    };
  }
  if (forced === "timed_out") {
    return {
      ...job,
      state: "timed_out",
      progress: 0.5,
      error: "Commissioning timed out",
      events: ["Starting…", "Connecting…", "Timed out"],
    };
  }

  return {
    ...job,
    state: "succeeded",
    progress: 1.0,
    device_ref: "light.matter_test",
    events: ["Starting…", "Connecting…", "Paired"],
  };
}

/**
 * MSW handler set for the F-352 Matter commissioning endpoints.
 *
 * Register BEFORE `onboardingHandlers` (see file header) — though the GET /
 * DELETE handlers passthrough for non-matter job ids so the generic F-351
 * handler still works for those.
 */
export const matterHandlers = [
  http.post(MATTER_COMMISSION_RE, async ({ request }) => {
    let code = "";
    let codeKind: string | null = null;
    try {
      const body = (await request.json()) as {
        code?: string;
        code_kind?: string | null;
      };
      code = body.code ?? "";
      codeKind = body.code_kind ?? null;
    } catch {
      // No/invalid JSON body — treat as empty code.
    }

    // Reset poll state for a fresh job and apply any forced outcome encoded
    // in the pairing code so tests can drive a failure end-to-end.
    pollCounts.set(MATTER_JOB_ID, 0);
    if (code === FORCE_FAILED_CODE) {
      forcedOutcomes.set(MATTER_JOB_ID, "failed");
    } else if (code === FORCE_TIMEOUT_CODE) {
      forcedOutcomes.set(MATTER_JOB_ID, "timed_out");
    }

    return HttpResponse.json({ ...baseJob(), code_kind: codeKind });
  }),

  http.get(JOB_RE, ({ request }) => {
    const path = new URL(request.url).pathname;
    const jobId = path.split("/").filter(Boolean).pop() ?? "";

    // Only the stateful Matter job is owned here; everything else falls
    // through to the generic F-351 handler.
    if (jobId !== MATTER_JOB_ID) {
      return passthrough();
    }

    const poll = pollCounts.get(jobId) ?? 0;
    const snapshot = jobForPoll(jobId, poll);
    pollCounts.set(jobId, poll + 1);
    return HttpResponse.json(snapshot);
  }),

  http.delete(JOB_RE, ({ request }) => {
    const path = new URL(request.url).pathname;
    const jobId = path.split("/").filter(Boolean).pop() ?? "";

    if (jobId !== MATTER_JOB_ID) {
      return passthrough();
    }

    return HttpResponse.json({
      ...baseJob(),
      state: "cancelled",
      events: ["Starting…", "Cancelled"],
    });
  }),
];
